import { getGlobalLocale, setGlobalLocale, testLocalStorage } from '@niaratech/niara-js-commons'
import queryString from 'query-string'
import React, { useContext, useState } from 'react'
import useImmutableFunction from '../hooks/useImmutableFunction'
import usePreviousIfEquals from '../hooks/usePreviousIfEquals'

const LOCAL_STORAGE_AVAILABLE = testLocalStorage()
interface Context {
  locale: string
  setLocale?: React.Dispatch<React.SetStateAction<string>>
  forceLocale?: { (locale: string) }
  setDefaultLocale?: { (locale: string) }
}

const DEFAULT_CONTEXT = { locale: 'pt-BR' }
export const I18nContext = React.createContext<Context>(DEFAULT_CONTEXT)

export const useI18nContext = () => {
  return useContext(I18nContext)
}

const fixLocale = (l: string): string | undefined => {
  if (!l) return undefined
  if (l == 'null' || l == 'undefined') return undefined
  // valida o formato da string. Ex: en ou pt-BR, são válidos. EN e pt-br são inválidos.
  if (!l.match(/^[a-z]{2}([-][A-Z]{2})?([-]([A-Z]{2}))?$/)) return undefined
  return l
}

const queryParams = window?.location?.search && queryString.parse(window.location.search)
const forcedLocale = fixLocale(
  !queryParams?.locale
    ? undefined
    : typeof queryParams?.locale === 'string'
    ? queryParams?.locale
    : queryParams?.locale?.[0]
)

if (forcedLocale && localStorage && LOCAL_STORAGE_AVAILABLE) {
  localStorage?.setItem('locale', forcedLocale)
}
let savedLocale = fixLocale(forcedLocale ?? LOCAL_STORAGE_AVAILABLE ? localStorage?.getItem('locale') : undefined)

if (savedLocale) {
  setGlobalLocale(savedLocale)
}

const I18nProvider = (props: React.PropsWithChildren<{ locale?: string }>) => {
  const [locale, setLocale] = useState(
    () => forcedLocale || savedLocale || props.locale || getGlobalLocale() || 'pt-BR'
  )

  setGlobalLocale(locale)
  const forceLocale: { (locale: string) } = useImmutableFunction((l) => {
    const locale = fixLocale(l)
    setLocale(locale)
    setGlobalLocale(locale)
    if (LOCAL_STORAGE_AVAILABLE) {
      if (locale) {
        localStorage.setItem('locale', locale)
      } else {
        localStorage.removeItem('locale')
      }
    }
    savedLocale = locale
  })
  // Substitui o locale default - mas não sobrescreve forcedLocale ou savedLocale
  const setDefaultLocale: { (locale: string) } = useImmutableFunction((locale) => {
    setLocale(() => forcedLocale || savedLocale || locale)
    setGlobalLocale(forcedLocale || savedLocale || locale)
  })
  const value = usePreviousIfEquals({ locale, setLocale, forceLocale, setDefaultLocale })
  const wrappingContext = useI18nContext()
  const hasWrappingContext = wrappingContext != null && wrappingContext !== DEFAULT_CONTEXT
  return (
    <I18nContext.Provider value={hasWrappingContext ? wrappingContext : value}>{props.children}</I18nContext.Provider>
  )
}

export default I18nProvider
